import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { getMatchItemVisibleText } from '@/shared/quiz/matchPairDisplay';
import { alertDialog, confirmDialog } from '@/ui_v3';

import { hasDuplicateMatchTexts } from '../components/EditableQuizCard/Match/matchDuplicate';
import { editingQuizIdAtom } from '../components/focus/editModeAtom';
import { focusField } from '../components/focus/focusUtils';
import { useBuildFormContext } from '../form/schema';
import {
  AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE,
  BuildQuizListFormSchema,
  getFirstQuizValidationErrorPath,
  validateQuizList,
} from '../form/schema/BuildQuizListSchema';

export const useValidateQuizList = () => {
  const setEditId = useSetAtom(editingQuizIdAtom);
  const { t } = useTranslation(['build']);

  const { getValues } = useBuildFormContext();

  const validate = async () => {
    const formData = getValues();

    const { success: quizListSuccess, error: quizListError } =
      BuildQuizListFormSchema.safeParse(formData);

    //먼저 퀴즈가 비어있는지 확인
    const isQuizEmpty =
      !quizListSuccess &&
      quizListError.errors.some(
        error => error.message === AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE,
      );

    if (isQuizEmpty) {
      await alertDialog.openAsync({
        header: {
          title: t('build:build.alert.noQuiz'),
        },
      });
      return false;
    }
    const { success, error } = validateQuizList(formData.quizList);

    if (!success) {
      await alertDialog.openAsync({
        header: {
          title: t('build:build.quiz.invalid'),
        },
      });

      const targetPath =
        getFirstQuizValidationErrorPath(error) ??
        error?.errors[0]?.path.join('.') ??
        '0';
      const targetIndex = Number(targetPath.split('.')[0]);

      const targetQuizId = formData.quizList[targetIndex]?.id;

      setEditId(targetQuizId);

      focusField(`quizList.${targetPath}`, {
        delay: 100,
        scroll: true,
        focus: true,
      });

      return false;
    }

    // 저장 직전 최종 게이트: 편집 중 안내는 우회 가능하므로, 같은 열에 중복 보기 텍스트가
    // 남아 있으면 저장 전 한 번 확인을 받는다.
    const hasMatchDuplicate = formData.quizList.some(quiz => {
      if (quiz.type !== 'match') return false;
      const leftTexts = quiz.matchData.leftItems.map(getMatchItemVisibleText);
      const rightTexts = quiz.matchData.rightItems.map(getMatchItemVisibleText);
      return (
        hasDuplicateMatchTexts(leftTexts) || hasDuplicateMatchTexts(rightTexts)
      );
    });

    if (hasMatchDuplicate) {
      const confirmed = await confirmDialog.openAsync({
        header: { title: t('build:build.match.duplicate.title') },
        body: t('build:build.match.duplicateSave.body'),
        cancelText: t('build:build.match.duplicate.cancel'),
        confirmText: t('build:build.match.duplicateSave.confirm'),
        shouldCloseOnDimClick: false,
      });

      if (!confirmed) return false;
    }

    return true;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(validate, [getValues]);
};
